
import { BlastEvent, SimulationConfig } from '../types';
import { getRadiusAtAngle } from './BreakUtils';

export const drawGravityDust = (
    ctx: CanvasRenderingContext2D,
    e: BlastEvent,
    config: SimulationConfig,
    progress: number,
    timeSeconds: number, // NEW param for real-time physics
    clampX?: (x: number) => number,
    clampY?: (y: number) => number,
    cam?: { x: number, y: number, scale: number },
    width?: number,
    height?: number
) => {
    // OPTIMIZATION: Reduce segment count based on config
    const density = config.dustResolution ?? 0.5;
    const maxParticles = config.maxDustParticles ?? 100;
    
    // Approximate perimeter
    const perimeter = 2 * Math.PI * e.maxRadius;
    
    let targetSegmentCount = Math.floor((perimeter / 2) * density);
    const segments = Math.min(targetSegmentCount, maxParticles);
    
    if (segments < 3) return;

    // Optimization: Pre-calculate screen bounds in world space if needed
    let minX = -Infinity, maxX = Infinity, minY = -Infinity, maxY = Infinity;
    const usePartialOpt = config.enableFrustumCulling && config.partialRingBlastOptimization && cam && width && height;
    if (usePartialOpt) {
        const halfW = width / 2;
        const halfH = height / 2;
        const scale = cam.scale || 1;
        const margin = config.frustumCullingMargin || 100;
        minX = -halfW / scale - cam.x - margin;
        maxX = halfW / scale - cam.x + margin;
        minY = -halfH / scale - cam.y - margin;
        maxY = halfH / scale - cam.y + margin;
    }

    // Use REAL TIME for physics, not progress
    const t = timeSeconds; 
    
    const g = config.shatterGravity ?? 500;
    const force = config.shatterForce ?? 100;
    
    // Alpha still uses progress so it eventually fades out at the end of lifecycle
    const alpha = Math.max(0, 1 - Math.pow(progress, 2)); // Fades later
    if (alpha <= 0.01) return;

    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(1, config.escapeRingThickness);
    // ctx.strokeStyle is already set by drawBlastEvent in BreakAnimations.ts
    // which handles real-time RGB toggles. We don't override it here.
    ctx.globalAlpha = alpha;
    
    ctx.beginPath();
    
    for(let i = 0; i < segments; i++) {
        const a1 = (i / segments) * Math.PI * 2;
        const a2 = ((i + 1) / segments) * Math.PI * 2;
        
        const r1 = getRadiusAtAngle(a1, e, e.maxRadius);
        const r2 = getRadiusAtAngle(a2, e, e.maxRadius);
        
        const p1x = Math.cos(a1) * r1;
        const p1y = Math.sin(a1) * r1;
        const p2x = Math.cos(a2) * r2;
        const p2y = Math.sin(a2) * r2;
        
        const midX = (p1x + p2x) / 2;
        const midY = (p1y + p2y) / 2;
        
        const len = Math.sqrt(midX*midX + midY*midY);
        const nx = len > 0 ? midX/len : 0;
        const ny = len > 0 ? midY/len : 0;
        
        const speed = force; 
        
        const vx = nx * speed;
        const vy = ny * speed;
        
        // Physics: v*t + 0.5*a*t^2
        const deltaX = vx * t;
        const deltaY = vy * t + 0.5 * g * t * t;
        
        let centerX = e.pos.x + midX + deltaX;
        let centerY = e.pos.y + midY + deltaY;

        if (usePartialOpt) {
            if (centerX < minX || centerX > maxX || centerY < minY || centerY > maxY) continue;
        }

        if (clampX) centerX = clampX(centerX);
        if (clampY) centerY = clampY(centerY);

        const seg1x = p1x - midX;
        const seg1y = p1y - midY;
        const seg2x = p2x - midX;
        const seg2y = p2y - midY;
        
        ctx.moveTo(centerX + seg1x, centerY + seg1y);
        ctx.lineTo(centerX + seg2x, centerY + seg2y);
    }
    ctx.stroke();
};
